import { styled } from '@mui/material/styles';
import { Box, Paper, Menu, Tooltip, IconButton } from '@mui/material';
import { motion } from 'framer-motion';

// Define z-index constants to maintain consistency
export const Z_INDEX = {
  CHAT_CONTAINER: 999999,
  DROPDOWNS: 9999999,
  TOOLTIPS: 99999999
} as const;

export const ChatContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isFullscreen' && prop !== 'isSidePanel'
})<{ 
  isFullscreen?: boolean;
  isSidePanel?: boolean;
}>(({ theme, isFullscreen, isSidePanel }) => ({
  width: '100%',
  maxWidth: isFullscreen ? '100%' : isSidePanel ? '31vw' : '31vw',
  height: isFullscreen ? '100vh' : isSidePanel ? '100vh' : '78vh',
  // Minimum width for chat-mode
  ...((!isFullscreen && !isSidePanel) && {
    minWidth: '380px',
  }),
  // Minimum width for sidepanel
  ...((!isFullscreen && isSidePanel) && {
    minWidth: '450px',
  }),
  display: 'flex',
  flexDirection: 'column',
  position: 'fixed',
  bottom: isFullscreen || isSidePanel ? '0' : '20px',
  right: isFullscreen ? '0' : isSidePanel ? '0' : '20px',
  borderWidth: isFullscreen ? '0' : theme.customProps.borderWidth,
  borderColor: theme.customProps.primaryColor,
  borderStyle: 'solid',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  backgroundColor: '#fff',
  overflow: 'hidden',
  zIndex: Z_INDEX.CHAT_CONTAINER,
  transformOrigin: 'bottom right',
  animation: 'expandChat 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '@keyframes expandChat': {
    '0%': {
      transform: 'scale(0.3)',
      opacity: 0,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1,
    },
  },

  '&.closing': {
    transform: 'scale(0.3)',
    opacity: 0,
  },

  '& .MuiTooltip-popper': {
    zIndex: `${Z_INDEX.TOOLTIPS} !important`,
  },
  '& .MuiTooltip-tooltip': {
    zIndex: `${Z_INDEX.TOOLTIPS} !important`,
  },
  ...(isSidePanel && {
    borderRightWidth: 0,
    height: '100vh',
  }),
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    height: '100%',
    bottom: 0,
    right: 0,
    borderWidth: 0,
  },
}));

export const MessageList = styled(Box)({
    flex: 1,
    overflow: 'auto',
    padding: '16px',
    '& > *': {
        maxWidth: '800px',
        margin: '0 auto',
    },
    overflowX: 'hidden',
});

export const InputContainer = styled(Box)({
    display: 'flex',
    padding: '16px',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    alignItems: 'center',
});

export const MessageBubble = styled(motion.div)<{ 
  isUser: boolean; 
  isFullscreen?: boolean;
  isSidePanel?: boolean;
  isStreaming?: boolean;
}>(({ isUser, isFullscreen, isSidePanel, isStreaming, theme }) => ({
  width: isUser ? 'fit-content' : 'auto',
  padding: '12px 16px',
  borderRadius: '16px',
  backgroundColor: isUser ? theme.customProps.primaryColor : '#f5f7fb',
  color: isUser ? '#fff' : 'inherit',
  marginLeft: isUser ? 'auto' : '0',
  marginBottom: '24px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',

  // Base styles for widget mode
  maxWidth: isUser ? '80%' : '95%',
  marginRight: isUser ? '0' : '8px',

  // Override styles for fullscreen/sidepanel
  ...((isFullscreen || isSidePanel) && {
    [theme.breakpoints.up('sm')]: {
      marginRight: isUser ? (isSidePanel ? '0%' : '25%') : '0',
      ...(!isUser && {
        marginLeft: isSidePanel ? '0%' : '25%',
      }),
      maxWidth: isSidePanel ? '80%' : '50%',
    },
  }),

  // Preserve table styling
  '& .MuiTableContainer-root': {
    margin: '12px -16px',
    width: 'calc(100% + 32px)',
    backgroundColor: isUser ? 'rgba(255, 255, 255, 0.1)' : '#fff',
    borderRadius: '8px',
    overflow: 'hidden',
  },

  // Preserve Plotly chart styling
  '& .plotly-chart': {
    margin: '12px -16px',
    width: 'calc(100% + 32px)',
    backgroundColor: '#fff',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid rgba(0,0,0,0.1)',
  },

  // Code block styling
  '& code': {
    backgroundColor: isUser ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
    padding: '2px 4px',
    borderRadius: '4px',
  },

  // Pre block styling
  '& pre': {
    margin: '12px -16px',
    padding: '16px',
    backgroundColor: isUser ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
    overflow: 'auto',
  },

  // Links styling
  '& a': {
    color: isUser ? '#fff' : theme.customProps.primaryColor,
    textDecoration: 'underline',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    display: 'inline-block',
    maxWidth: '100%',
  },
  
  // Invalid links (shown as plain text)
  '& .invalid-link': {
    color: 'inherit',
    textDecoration: 'none',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    display: 'inline-block',
    maxWidth: '100%',
  },
  
  // Links in validation process (shown as plain text)
  '& .validating-link': {
    color: 'inherit',
    textDecoration: 'none',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    display: 'inline-block',
    maxWidth: '100%',
  },
  
  // Streaming links (plain text during streaming)
  '& .streaming-link': {
    color: 'inherit',
    textDecoration: 'none',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    display: 'inline-block',
    maxWidth: '100%',
  },

  // Cursor animation
  '@keyframes blink': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0 },
    '100%': { opacity: 1 },
  },
  '& .cursor': {
    display: isStreaming ? 'inline-block' : 'none',
    animation: 'blink 1s infinite',
    marginLeft: '2px',
  },
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
}));

export const TypingIndicator = styled(Box)<{ 
  isUser: boolean; 
  isFullscreen?: boolean;
  isSidePanel?: boolean;
}>(({ isUser, isFullscreen, isSidePanel, theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px',
    '& .dot': {
        width: '8px',
        height: '8px',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        borderRadius: '50%',
        animation: 'bounce 1.4s infinite ease-in-out',
        '&:nth-of-type(1)': {
            animationDelay: '0s',
        },
        '&:nth-of-type(2)': {
            animationDelay: '0.2s',
        },
        '&:nth-of-type(3)': {
            animationDelay: '0.4s',
        },
    },
    '@keyframes bounce': {
        '0%, 80%, 100%': {
            transform: 'scale(0)',
        },
        '40%': {
            transform: 'scale(1)',
        },
    },
    // Desktop fullscreen/side panel mode margins
    ...((isFullscreen || isSidePanel) && {
        [theme.breakpoints.up('sm')]: {
            marginRight: isUser ? (isSidePanel ? '10%' : '25%') : '0',
            ...(!isUser && {
                marginLeft: isSidePanel ? '10%' : '25%',
            }),
            maxWidth: isSidePanel ? '80%' : '50%',
            width: 'fit-content',
        },
    }),
}));

export const WelcomeMessage = styled(motion.div)({
    textAlign: 'center',
    padding: '40px 20px',
    maxWidth: '600px',
    margin: '0 auto',
    '& h1': {
        fontSize: '2rem',
        marginBottom: '16px',
    },
});

export const messageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, x: -20 },
};

export const StyledMenu = styled(Menu)({
  '&.MuiPopover-root': {
    zIndex: `${Z_INDEX.DROPDOWNS} !important`,
  },
  '& .MuiPopover-paper': {
    zIndex: `${Z_INDEX.DROPDOWNS} !important`,
    minWidth: '120px',
    maxWidth: '200px',
    width: 'auto',
  },
  '& .MuiIconButton-root': {
    transition: 'all 0.2s ease-in-out',
    color: '#1a237e',
    '&:hover': {
      transform: 'scale(1.15)',
      backgroundColor: 'rgba(26, 35, 126, 0.08)',
      color: '#303f9f',
    },
    '&:active': {
      transform: 'scale(0.95)',
      backgroundColor: 'rgba(26, 35, 126, 0.12)',
    },
  },
});

export const StyledTooltip = styled(Tooltip)({
  '&.MuiTooltip-popper': {
    zIndex: `${Z_INDEX.TOOLTIPS} !important`,
    width: 'auto',
  },
  '& .MuiTooltip-tooltip': {
    zIndex: `${Z_INDEX.TOOLTIPS} !important`,
    maxWidth: '200px',
  },
  // Add portal styles
  '& .MuiTooltip-popper[data-popper-placement*="top"]': {
    zIndex: `${Z_INDEX.TOOLTIPS} !important`,
    width: 'auto',
  },
  '& .MuiTooltip-popper[data-popper-placement*="bottom"]': {
    zIndex: `${Z_INDEX.TOOLTIPS} !important`,
    width: 'auto',
  },
  '& .MuiIconButton-root': {
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.15)',
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
  },
});

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  transition: 'all 0.2s ease-in-out',
  color: '#1a237e',
  '&:hover': {
    transform: 'scale(1.15)',
    backgroundColor: 'rgba(26, 35, 126, 0.08)',
    color: '#303f9f',
  },
  '&:active': {
    transform: 'scale(0.95)',
    backgroundColor: 'rgba(26, 35, 126, 0.12)',
  },
  '&.feedback-clicked': {
    animation: 'feedbackPulse 0.5s ease-in-out',
  },
  '@keyframes feedbackPulse': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.2)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
}));

// Add new animation for the floating button
export const FloatingButton = styled(motion.div)({
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  zIndex: Z_INDEX.CHAT_CONTAINER,
  transformOrigin: 'center',
  animation: 'expandButton 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '@keyframes expandButton': {
    '0%': {
      transform: 'scale(0.3)',
      opacity: 0,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1,
    },
  },
});

export const MarkdownWrapper = styled('div')({
  width: '100%',
  '& *': {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  '& a': {
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    display: 'inline-block',
    maxWidth: '100%',
  }
});